import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import HeroLogo from "./HeroLogo";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-[0.06em]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <section
      id="home"
      data-testid="hero-section"
      ref={ref}
      className="relative overflow-hidden"
    >
      <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-14 px-4 pb-20 pt-14 sm:px-6 sm:pt-20 lg:grid-cols-12 lg:gap-8 lg:px-8 lg:pb-28 lg:pt-28">
        <div className="lg:col-span-7">
          <motion.p
            data-testid="hero-chapter-tag"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-[#E05A10]"
          >
            00 <span className="text-[#8C857B]">//</span> Introduction
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-[#1C1B1A] sm:text-5xl lg:text-6xl"
          >
            <MaskedLine delay={0.25}>SAATH</MaskedLine>
            <MaskedLine delay={0.37}>Trainings</MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subline"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 font-display text-lg font-medium text-[#524E48] sm:text-xl"
          >
            Husain M, Founder &amp; Mentor
          </motion.p>

          <motion.p
            data-testid="hero-intro"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-4 max-w-xl text-base leading-relaxed text-[#2D2A26] sm:text-lg"
          >
            I train traders to understand stock market trading, with a special
            focus on practical options strategies.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-contact-me-button"
              onClick={() => scrollToId("contact")}
              className="group inline-flex min-h-[52px] items-center gap-2 rounded-full bg-[#E05A10] px-7 text-base font-semibold text-white shadow-[0_12px_30px_-10px_rgba(224,90,16,0.5)] transition-all duration-300 hover:bg-[#B43B00] active:scale-[0.97]"
            >
              Contact Me
              <ArrowRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
            <button
              data-testid="hero-explore-button"
              onClick={() => scrollToId("offerings")}
              className="link-underline inline-flex min-h-[52px] items-center gap-2 text-base font-medium text-[#524E48]"
            >
              See what I offer <ArrowDown size={16} />
            </button>
          </motion.div>
        </div>

        <motion.div style={{ y: portraitY }} className="lg:col-span-5">
          <motion.div
            data-testid="hero-portrait-frame"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto max-w-sm lg:max-w-none"
          >
            <div className="absolute -inset-2 rounded-[2rem] border border-[#E05A10]/40" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-[1.75rem] border border-[#E2D9CC] bg-white shadow-[0_30px_60px_-25px_rgba(28,27,26,0.25)]">
              <div
                className="pointer-events-none absolute inset-0 z-10"
                style={{
                  background:
                    "radial-gradient(120% 90% at 50% 0%, rgba(224,90,16,0.10) 0%, rgba(250,247,242,0) 55%)",
                }}
                aria-hidden="true"
              />
              <HeroLogo />
            </div>
            <div
              data-testid="hero-portrait-badge"
              className="absolute -bottom-5 left-1/2 w-max -translate-x-1/2 rounded-full border border-[#E2D9CC] bg-[#1C1B1A] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#FAF7F2] shadow-lg sm:text-xs"
            >
              Husain M <span className="text-[#E05A10]">•</span> Founder
              &amp; Mentor
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
